<template>
<div class="flex min-h-screen bg-gray-50 dark:bg-gray-900">

<!-- Sidebar -->
<AdminSidebar :isOpen="isSidebarOpen" />

<main class="flex-1 transition-all duration-300" :class="{'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen}">

<!-- Topbar -->
<header class="fixed top-0 w-full z-50 bg-white dark:bg-gray-800 shadow">
<AdminTopbar
:page-title="currentPageTitle"
:is-sidebar-open="isSidebarOpen"
:is-dark-mode="isDarkMode"
@toggle-sidebar="toggleSidebar"
@toggle-dark-mode="toggleDarkMode"
/>
</header>

<div class="p-6 mt-16">

<!-- Search + Add Button -->
<div class="flex justify-between mb-8 p-4">

<input
v-model="search"
type="text"
placeholder="Search users..."
class="border rounded-lg px-4 py-2 w-72 focus-outine-none focus dark:bg-gray-700 dark:text-white"
/>

<button
@click="openModal()"
class="bg-[#74bd54] text-white px-5 py-2 rounded-lg hover:bg-[#63a43f]"
>
+ Add User
</button>

</div>

<!-- Users Grid -->
<div v-if="filteredUsers.length" class="grid md:grid-cols-3 lg:grid-cols-4 gap-6">

<div
v-for="user in filteredUsers"
:key="user.id"
class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow hover:shadow-lg"
>

<div class="flex items-center space-x-3 mb-4">
<div class="w-12 h-12 bg-secondary text-white flex items-center justify-center rounded-full font-bold">
{{ user.initials }}
</div>

<div>
<h3 class="font-semibold text-gray-800 dark:text-white">{{ user.name }}</h3>
<span class="text-xs text-gray-500">{{ user.role }}</span>
</div>
</div>

<p class="text-sm text-gray-500 mb-6">{{ user.email }}</p>

<div class="flex justify-between text-sm">
<button @click="editUser(user)" class="text-blue-600 hover:underline">Edit</button>
<button @click="confirmDelete(user.id)" class="text-red-500 hover:underline">Delete</button>
  <ConfirmModal
    :show="showDeleteModal"
    title="Delete User"
    message="Are you sure you want to delete this user? This cannot be undone."
    confirmText="Delete"
    cancelText="Cancel"
    @confirm="handleDelete"
    @cancel="showDeleteModal = false"
  />
</div>

</div>
</div>

<div v-else class="text-center text-gray-400 mt-20">
No users found
</div>

<!-- Modal -->
<Transition name="modal">
<div v-if="showModal" class="fixed inset-0 bg-black/40 flex items-center justify-center">

<div class="bg-white dark:bg-gray-800 rounded-xl p-6 w-[420px]">

<h3 class="text-lg font-semibold text-[#035a76] mb-6">
{{ editing ? "Edit User" : "Add User" }}
</h3>

<form @submit.prevent="saveUser" class="space-y-4">

<input
v-model="form.name"
type="text"
placeholder="Full Name"
required
class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white"
/>

<input
v-model="form.email"
type="email"
placeholder="Email"
required
class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white"
/>

<input
v-model="form.password"
type="password"
placeholder="Password"
:required="!editing"
class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white"
/>

<select
v-model="form.role"
required
class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white"
>
<option disabled value="">Select Role</option>
<option>Admin</option>
<option>HR</option>
<option>Teacher</option>
<option>Staff</option>
<option>Com</option>

</select>

<div class="flex justify-end space-x-3">

<button type="button" @click="closeModal" class="px-4 py-2 bg-gray-300 rounded">Cancel</button>
<button 
  type="submit" 
  :disabled="isLoading"
  @click="saveUser"
  class="px-4 py-2 bg-[#74bd54] text-white rounded hover:bg-[#63a43f] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-[#74bd54] transition-colors flex items-center justify-center gap-2 min-w-[80px]"
>
  <!-- Spinner (show only when loading) -->
  <svg 
    v-if="isLoading" 
    class="animate-spin h-4 w-4 text-white" 
    xmlns="http://www.w3.org/2000/svg" 
    fill="none" 
    viewBox="0 0 24 24"
  >
    <circle 
      class="opacity-25" 
      cx="12" 
      cy="12" 
      r="10" 
      stroke="currentColor" 
      stroke-width="4"
    ></circle>
    <path 
      class="opacity-75" 
      fill="currentColor" 
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    ></path>
  </svg>

  <!-- Button text -->
  <span>{{ isLoading ? 'Saving...' : 'Save' }}</span>
</button>
</div>
</form>

</div>
</div>
</Transition>

</div>
</main>
</div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";

import api from "@/services/api";
import HrSidebar from "@/components/hr/HrSidebar.vue";
import HrTopbar from "@/components/hr/HrTopbar.vue";
import ConfirmModal from "@/components/hr/ConfirmModal.vue";


import { useToast } from 'vue-toastification'
import AdminSidebar from "../../components/admin/AdminSidebar.vue";
import AdminTopbar from "../../components/admin/AdminTopbar.vue";
const toast = useToast()
const isLoading = ref(false)

const currentPageTitle = ref("Users");
const isSidebarOpen = ref(true);
const isDarkMode = ref(false);

const showModal = ref(false);
const editing = ref(false);
const search = ref("");
const users = ref([]);

const showDeleteModal = ref(false);
const itemToDelete = ref(null);


const getInitials = (name) => {
  return name
    .split(" ")
    .map(n => n[0])
    .join("")
    .toUpperCase();
};

const fetchUsers = async () => {
  try {
    const response = await api.get("admin/Users.php");

    users.value = response.data.map(user => ({
      id: user.id,
      name: user.full_name,       
      email: user.email,
      role: user.user_type,       
      initials: getInitials(user.full_name)
    }));

  } catch (error) {
    console.error("Error fetching users:", error);
  }
};


const form = ref({ id:null, name:"", email:"", password:"", role:"", initials:"" });

const filteredUsers = computed(()=>{
return users.value.filter(u => 
u.name.toLowerCase().includes(search.value.toLowerCase()) ||
u.email.toLowerCase().includes(search.value.toLowerCase()) ||
u.role.toLowerCase().includes(search.value.toLowerCase())
);
})

const openModal=()=>{
editing.value=false;
form.value={id:null,name:"",email:"",password:"",role:"",initials:""}
showModal.value=true;
}

const editUser=(user)=>{
editing.value=true;
form.value={...user}
showModal.value=true;
}


const saveUser = async () => {
  if (isLoading.value) return;
  isLoading.value = true;

  const MIN_LOADING_MS = 2000; 
  const startTime = Date.now();

  // Show the toast immediately
  let loadingToastId = toast.info("Saving user...", { timeout: false });

  try {
    let response;

    if (editing.value) {
      response = await api.put("admin/Users.php", form.value);
    } else {
      response = await api.post("admin/Users.php", form.value);
    }

    // ← CHECK FOR BACKEND ERROR HERE
    if (response.data?.status === "error") {
      throw new Error(response.data.message || "Failed to save user");
    }

    // Only update frontend list if successful
    if (editing.value) {
      const index = users.value.findIndex(u => u.id === form.value.id);
      if (index !== -1) users.value[index] = { ...form.value };
      toast.success("User updated successfully!");
    } else {
      users.value.push({
        id: response.data.id,
        name: form.value.name,
        email: form.value.email,
        role: form.value.role
      });
      toast.success("User created successfully!");
    }

    // Minimum loading delay
    const elapsed = Date.now() - startTime;
    const remaining = Math.max(0, MIN_LOADING_MS - elapsed);
    if (remaining > 0) await new Promise(res => setTimeout(res, remaining));

    // Dismiss loading toast
    toast.dismiss(loadingToastId);

    closeModal();
    fetchUsers();

  } catch (error) {
    // Same minimum delay
    const elapsed = Date.now() - startTime;
    const remaining = Math.max(0, MIN_LOADING_MS - elapsed);
    if (remaining > 0) await new Promise(res => setTimeout(res, remaining));
const errorMsg = error.response?.data?.message || error.message || "An unexpected error occurred";
  toast.error(errorMsg);
    // Dismiss loading toast
    toast.dismiss(loadingToastId);

    // Show proper error toast
  } finally {
    isLoading.value = false;
  }
};


// Open modal
const confirmDelete = (id) => {
  itemToDelete.value = id;
  showDeleteModal.value = true;
};

const handleDelete = async () => {
  try {
    await api.delete("admin/Users.php", { data: { id: itemToDelete.value } });
    users.value = users.value.filter(u => u.id !== itemToDelete.value);
    toast.success("Deleted successfully!");
  } catch (err) {
    toast.error("Failed to delete.");
  } finally {
    showDeleteModal.value = false;
    itemToDelete.value = null;
  }
};

const closeModal=()=>{ showModal.value=false }

const toggleSidebar=()=>{ isSidebarOpen.value=!isSidebarOpen.value }
const toggleDarkMode=()=>{
isDarkMode.value=!isDarkMode.value;
if(isDarkMode.value) document.documentElement.classList.add("dark")
else document.documentElement.classList.remove("dark")
}
onMounted(() => {
  fetchUsers();
});
</script>

<style>
.modal-enter-active,
.modal-leave-active{ transition:all .25s ease; }
.modal-enter-from{ opacity:0; transform:scale(.9); }
.modal-leave-to{ opacity:0; transform:scale(.9); }
</style>